/**
 * JWT Authentication utilities for dojo.
 *
 * Ported from catalog-frontend/src/lib/jwt-auth.ts during the 2026-05-23
 * migration away from Supabase Auth (sb-* cookies) to Genkan-signed
 * JWT (extenshi-* cookies). See PR for context.
 *
 * Wire contract with Genkan:
 *   - HttpOnly  cookie `extenshi-auth-token`    — JWT access token, 24h
 *   - HttpOnly  cookie `extenshi-refresh-token` — rotated, 7d
 *   - Readable  cookie `extenshi-user-data`     — base64url-encoded
 *                                                  UserDataCookie, 24h
 *
 * Endpoints on `NEXT_PUBLIC_AUTH_URL` (Genkan):
 *   POST /api/jwt/verify   { token? }          → { valid, user, ... }
 *   POST /api/jwt/refresh  ()                  → 200 + Set-Cookie
 *   DELETE /auth/logout    ()                  → 200 + clears cookies
 */

export interface ExtenshiJWTPayload {
	userId: string
	email: string
	role: string
	permissions: string[]
	iat: number
	exp: number
	iss: string
	aud: string
}

export interface UserDataCookie {
	userId: string
	email: string
	role: string
	permissions: string[]
	expiresAt: number
}

export const COOKIE_NAMES = {
	AUTH_TOKEN: 'extenshi-auth-token',
	REFRESH_TOKEN: 'extenshi-refresh-token',
	USER_DATA: 'extenshi-user-data',
} as const

export const LOCAL_STORAGE_KEYS = {
	AUTH_TOKEN: 'extenshi_local_auth_token',
	USER_DATA: 'extenshi_local_user_data',
} as const

function isLocalDev(): boolean {
	if (typeof window === 'undefined') return false
	const hostname = window.location.hostname
	return hostname === 'localhost' || hostname === '127.0.0.1'
}

function getCookie(name: string): string | null {
	if (typeof document === 'undefined') return null
	const value = `; ${document.cookie}`
	const parts = value.split(`; ${name}=`)
	if (parts.length === 2) {
		return parts.pop()?.split(';').shift() || null
	}
	return null
}

function getLocalStorage(key: string): string | null {
	if (typeof window === 'undefined') return null
	try {
		return localStorage.getItem(key)
	} catch {
		return null
	}
}

function setLocalStorage(key: string, value: string): void {
	if (typeof window === 'undefined') return
	try {
		localStorage.setItem(key, value)
	} catch {
		console.error('Failed to set localStorage')
	}
}

function removeLocalStorage(key: string): void {
	if (typeof window === 'undefined') return
	try {
		localStorage.removeItem(key)
	} catch {
		console.error('Failed to remove from localStorage')
	}
}

/**
 * Extract auth tokens from URL parameters (for local dev redirect).
 * Genkan appends `_authToken` and `_userData` to the redirect URL on
 * localhost because cookies are port-scoped there. Returns true if
 * tokens were found and saved.
 */
export function extractAndSaveAuthFromUrl(): boolean {
	if (typeof window === 'undefined') return false
	if (!isLocalDev()) return false

	const url = new URL(window.location.href)
	const authToken = url.searchParams.get('_authToken')
	const userData = url.searchParams.get('_userData')

	if (authToken && userData) {
		setLocalStorage(LOCAL_STORAGE_KEYS.AUTH_TOKEN, authToken)
		setLocalStorage(LOCAL_STORAGE_KEYS.USER_DATA, userData)
		url.searchParams.delete('_authToken')
		url.searchParams.delete('_userData')
		window.history.replaceState({}, '', url.toString())
		return true
	}

	return false
}

function decodeBase64Url(base64url: string): string {
	let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
	const padding = base64.length % 4
	if (padding) {
		base64 += '='.repeat(4 - padding)
	}
	return decodeURIComponent(
		atob(base64)
			.split('')
			.map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
			.join(''),
	)
}

export function parseUserDataCookie(cookieValue?: string): UserDataCookie | null {
	try {
		let value = cookieValue || getCookie(COOKIE_NAMES.USER_DATA)
		if (!value && isLocalDev()) {
			value = getLocalStorage(LOCAL_STORAGE_KEYS.USER_DATA)
		}
		if (!value) return null

		const decoded = decodeBase64Url(value)
		const userData = JSON.parse(decoded) as UserDataCookie

		if (userData.expiresAt < Date.now()) {
			if (isLocalDev()) {
				removeLocalStorage(LOCAL_STORAGE_KEYS.USER_DATA)
				removeLocalStorage(LOCAL_STORAGE_KEYS.AUTH_TOKEN)
			}
			return null
		}

		return userData
	} catch (error) {
		console.error('Failed to parse user data:', error)
		return null
	}
}

function isJWTExpired(token: string): boolean {
	try {
		const parts = token.split('.')
		if (parts.length !== 3) return true
		const payload = JSON.parse(decodeBase64Url(parts[1]))
		if (!payload.exp) return true
		// 30-second safety margin so we don't ship a request with a JWT
		// that the BFF will reject between our isExpired() check and the
		// server-side validation.
		return payload.exp < Math.floor(Date.now() / 1000) + 30
	} catch {
		return true
	}
}

export function getJWTToken(): string | null {
	let token = getCookie(COOKIE_NAMES.AUTH_TOKEN)
	if (!token && isLocalDev()) {
		token = getLocalStorage(LOCAL_STORAGE_KEYS.AUTH_TOKEN)
	}
	if (!token) return null

	if (isJWTExpired(token)) {
		if (isLocalDev()) {
			removeLocalStorage(LOCAL_STORAGE_KEYS.AUTH_TOKEN)
			removeLocalStorage(LOCAL_STORAGE_KEYS.USER_DATA)
		}
		return null
	}
	return token
}

export async function verifyJWTWithAuthService(token?: string): Promise<ExtenshiJWTPayload | null> {
	try {
		const tokenToVerify = token || getJWTToken()
		const authServiceUrl = process.env.NEXT_PUBLIC_AUTH_URL || 'http://localhost:3005'

		if (!tokenToVerify && isLocalDev()) {
			return null
		}

		const response = await fetch(`${authServiceUrl}/api/jwt/verify`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			body: JSON.stringify({ token: tokenToVerify || undefined }),
		})

		if (!response.ok) return null

		const data = await response.json()
		if (data.valid && data.user) {
			return {
				userId: data.user.id,
				email: data.user.email,
				role: data.user.role,
				permissions: data.user.permissions,
				iat: Math.floor(data.issuedAt / 1000),
				exp: Math.floor(data.expiresAt / 1000),
				iss: 'extenshi-auth',
				aud: 'extenshi.io',
			}
		}
		return null
	} catch (error) {
		if (error instanceof TypeError && error.message === 'Failed to fetch') {
			console.debug('Auth service unavailable - running in unauthenticated mode')
		} else {
			console.debug('JWT verification error:', error)
		}
		return null
	}
}

export async function refreshJWTTokens(): Promise<boolean> {
	try {
		const authServiceUrl = process.env.NEXT_PUBLIC_AUTH_URL || 'http://localhost:3005'
		const response = await fetch(`${authServiceUrl}/api/jwt/refresh`, {
			method: 'POST',
			credentials: 'include',
		})
		return response.ok
	} catch (error) {
		console.error('Error refreshing tokens:', error)
		return false
	}
}

/**
 * Flag to prevent background verify/refresh from restoring a logged-out
 * session. Set on signOut, cleared on next explicit login.
 */
let signedOutInThisSession = false

export function wasSignedOut(): boolean {
	return signedOutInThisSession
}

export function clearSignedOutFlag(): void {
	signedOutInThisSession = false
}

export async function signOutJWT(): Promise<boolean> {
	try {
		signedOutInThisSession = true

		if (isLocalDev()) {
			removeLocalStorage(LOCAL_STORAGE_KEYS.AUTH_TOKEN)
			removeLocalStorage(LOCAL_STORAGE_KEYS.USER_DATA)
		}

		// Wipe the non-HttpOnly USER_DATA cookie from JS — HttpOnly
		// AUTH_TOKEN and REFRESH_TOKEN need the server-side logout
		// endpoint to clear them via Set-Cookie.
		if (typeof document !== 'undefined') {
			const hostname = window.location.hostname
			const cookieDomains = [hostname, `.${hostname}`, '']
			for (const name of Object.values(COOKIE_NAMES)) {
				for (const domain of cookieDomains) {
					const domainPart = domain ? `; domain=${domain}` : ''
					// biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API lacks Safari support; this is existing auth cleanup infra
					document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`
				}
			}
		}

		const authServiceUrl = process.env.NEXT_PUBLIC_AUTH_URL || 'http://localhost:3005'
		const response = await fetch(`${authServiceUrl}/auth/logout`, {
			method: 'DELETE',
			credentials: 'include',
		})
		return response.ok
	} catch (error) {
		console.error('Error signing out:', error)
		if (isLocalDev()) {
			removeLocalStorage(LOCAL_STORAGE_KEYS.AUTH_TOKEN)
			removeLocalStorage(LOCAL_STORAGE_KEYS.USER_DATA)
		}
		return false
	}
}

export function isUserDataValid(userData: UserDataCookie | null): boolean {
	return userData !== null && userData.expiresAt > Date.now()
}

export function getAuthLoginUrl(returnUrl?: string): string {
	const authServiceUrl = process.env.NEXT_PUBLIC_AUTH_URL || 'http://localhost:3005'
	const currentUrl = typeof window !== 'undefined' ? window.location.href : ''
	const redirectUrl = returnUrl || currentUrl
	const params = new URLSearchParams()
	if (redirectUrl) {
		params.set('redirect', redirectUrl)
	}
	return `${authServiceUrl}/auth/login${params.toString() ? `?${params.toString()}` : ''}`
}

export function redirectToLogin(returnUrl?: string): void {
	window.location.href = getAuthLoginUrl(returnUrl)
}
